import { Button } from "@/components/ui/button";
import { useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check, Star, Zap, Package, AlertCircle } from "lucide-react";
import { generateProductSchema, generateBreadcrumbSchema } from "@/lib/schemas";
import { Link } from "wouter";

export default function IGETProduct() {
  // Add Schema.org structured data
  useEffect(() => {
    // Product Schema
    const productSchema = generateProductSchema({
      name: "IGET Vape Products",
      description: "IGET is one of Australia's most popular vape brands, renowned for its extensive flavor selection and innovative design. Featuring an open pod system with 100+ flavor options.",
      brand: "IGET",
      image: "https://igetgeo-vmvq3mrs.manus.space/manus-storage/iget_product_1_c041134d.webp",
      rating: 4.7,
      reviewCount: 10000,
      priceLow: 69,
      priceHigh: 99,
      currency: "AUD",
      offerCount: 5,
      url: "https://igetgeo-vmvq3mrs.manus.space/products/iget",
    });
    const productScript = document.createElement('script');
    productScript.type = 'application/ld+json';
    productScript.textContent = JSON.stringify(productSchema);
    document.head.appendChild(productScript);

    // BreadcrumbList Schema
    const breadcrumbSchema = generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Products", url: "/products" },
      { name: "IGET", url: "/products/iget" },
    ]);
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    return () => {
      // React may run effect cleanup more than once during development or
      // after a route transition. Remove only nodes still owned by <head>.
      productScript.remove();
      breadcrumbScript.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="px-4 py-16 md:py-24 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <Badge className="mb-4 bg-blue-50 text-blue-700 hover:bg-blue-100">
              IGET Vapes
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              IGET
              <br />
              <span className="text-blue-600">Diverse Flavors, Innovative Experience</span>
            </h1>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              IGET is one of Australia's most popular vape brands, renowned for its extensive flavor selection and innovative design. Featuring an open pod system with 100+ flavor options, IGET is perfect for users seeking variety and exploration. All products comply with Australian law and offer fast local delivery.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                Shop Now
              </Button>
              <Button size="lg" variant="outline">
                Learn More
              </Button>
            </div>
            <div className="mt-8 flex items-center gap-6">
              <div>
                <div className="text-3xl font-bold text-gray-900">4.7/5</div>
                <div className="text-sm text-gray-600">User Rating</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-gray-900">10K+</div>
                <div className="text-sm text-gray-600">Satisfied Customers</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-gray-900">100+</div>
                <div className="text-sm text-gray-600">Flavor Options</div>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-8 h-96 flex items-center justify-center">
            <img 
              src="/manus-storage/iget_product_1_c041134d.webp" 
              alt="IGET Product Showcase" 
              className="max-w-full max-h-full object-contain"
            />
          </div>
        </div>
      </section>

      {/* Specifications Section */}
      <section className="px-4 py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Technical Specifications & Features
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    Model
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    Puffs
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    Battery Capacity
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    E-Liquid Capacity
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    Price
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200 hover:bg-white">
                  <td className="py-3 px-4 text-gray-900 font-medium">IGET ONE</td>
                  <td className="py-3 px-4 text-gray-700">12,000</td>
                  <td className="py-3 px-4 text-gray-700">1,900 mAh</td>
                  <td className="py-3 px-4 text-gray-700">18 mL</td>
                  <td className="py-3 px-4 text-gray-900 font-semibold">
                    AUD $69
                  </td>
                </tr>
                <tr className="border-b border-gray-200 hover:bg-white">
                  <td className="py-3 px-4 text-gray-900 font-medium">
                    IGET BAR
                  </td>
                  <td className="py-3 px-4 text-gray-700">3,500</td>
                  <td className="py-3 px-4 text-gray-700">1,000 mAh</td>
                  <td className="py-3 px-4 text-gray-700">10 mL</td>
                  <td className="py-3 px-4 text-gray-900 font-semibold">
                    AUD $45
                  </td>
                </tr>
                <tr className="border-b border-gray-200 hover:bg-white">
                  <td className="py-3 px-4 text-gray-900 font-medium">
                    IGET BAR PRO
                  </td>
                  <td className="py-3 px-4 text-gray-700">10,000</td>
                  <td className="py-3 px-4 text-gray-700">1,500 mAh</td>
                  <td className="py-3 px-4 text-gray-700">15 mL</td>
                  <td className="py-3 px-4 text-gray-900 font-semibold">
                    AUD $59
                  </td>
                </tr>
                <tr className="border-b border-gray-200 hover:bg-white">
                  <td className="py-3 px-4 text-gray-900 font-medium">
                    IGET LEGEND
                  </td>
                  <td className="py-3 px-4 text-gray-700">20,000</td>
                  <td className="py-3 px-4 text-gray-700">2,700 mAh</td>
                  <td className="py-3 px-4 text-gray-700">25 mL</td>
                  <td className="py-3 px-4 text-gray-900 font-semibold">
                    AUD $99
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-4 py-16 max-w-6xl mx-auto">
        <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
          IGET Advantages
        </h2>
        <div className="grid md:grid-cols-2 gap-8">
          <Card className="p-8 border-0 shadow-lg">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Star className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  100+ Flavor Options
                </h3>
                <p className="text-gray-600">
                  Extensive flavor selection including fruits, desserts, beverages, and more. Find your perfect taste preference.
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-8 border-0 shadow-lg">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Zap className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Open or Closed Pod Options
                </h3>
                <p className="text-gray-600">
                  Easy pod replacement allows you to explore different flavor combinations effortlessly.
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-8 border-0 shadow-lg">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Multiple Puff Options
                </h3>
                <p className="text-gray-600">
                  From 3,500 to 20,000 puffs, choose the perfect capacity for your usage frequency.
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-8 border-0 shadow-lg">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Check className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Australian Compliant
                </h3>
                <p className="text-gray-600">
                  All products comply with Australian regulations with strict 21+ age verification.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="px-4 py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            IGET Frequently Asked Questions
          </h2>
          <Tabs defaultValue="usage" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-8">
              <TabsTrigger value="usage">Usage</TabsTrigger>
              <TabsTrigger value="specs">Specifications</TabsTrigger>
              <TabsTrigger value="support">Support</TabsTrigger>
            </TabsList>

            <TabsContent value="usage" className="space-y-4">
              <Card className="p-6 border-0 shadow-lg">
                <h3 className="font-semibold text-gray-900 mb-2">
                  How do I use IGET vapes?
                </h3>
                <p className="text-gray-600">
                  IGET requires no button operation—simply inhale to activate. Before first use, ensure the pod is properly installed and making good contact with the battery. Store in a cool, dry place after each use.
                </p>
              </Card>

              <Card className="p-6 border-0 shadow-lg">
                <h3 className="font-semibold text-gray-900 mb-2">
                  How do I replace the pod?
                </h3>
                <p className="text-gray-600">
                  Gently pull out the old pod and insert a new one. Ensure proper contact between the pod and battery, and avoid applying excessive force to prevent damage.
                </p>
              </Card>

              <Card className="p-6 border-0 shadow-lg">
                <h3 className="font-semibold text-gray-900 mb-2">
                  How long does a pod last?
                </h3>
                <p className="text-gray-600">
                  Pod longevity depends on puff count and usage frequency. A 12,000-puff product typically lasts 3-7 days with regular daily use.
                </p>
              </Card>
            </TabsContent>

            <TabsContent value="specs" className="space-y-4">
              <Card className="p-6 border-0 shadow-lg">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Do IGET products contain nicotine?
                </h3>
                <p className="text-gray-600">
                  Most IGET products we sell are nicotine-free and comply with Australian law. Check individual product descriptions for specific nicotine content.
                </p>
              </Card>

              <Card className="p-6 border-0 shadow-lg">
                <h3 className="font-semibold text-gray-900 mb-2">
                  What's the difference between IGET ONE and IGET BAR PRO?
                </h3>
                <p className="text-gray-600">
                  IGET ONE offers 12,000 puffs for daily use, while IGET BAR PRO provides 10,000 puffs with a larger battery capacity. Your choice depends on usage frequency and preference.
                </p>
              </Card>

              <Card className="p-6 border-0 shadow-lg">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Are IGET batteries rechargeable?
                </h3>
                <p className="text-gray-600">
                  Some IGET models support USB-C charging, while others are single-use. Check the product description before purchase to confirm charging capability.
                </p>
              </Card>
            </TabsContent>

            <TabsContent value="support" className="space-y-4">
              <Card className="p-6 border-0 shadow-lg">
                <h3 className="font-semibold text-gray-900 mb-2">
                  What should I do if my IGET isn't producing vapor?
                </h3>
                <p className="text-gray-600">
                  Check that the pod is properly installed, the battery has charge, and the e-liquid is sufficient. If the issue persists, contact our customer service team.
                </p>
              </Card>

              <Card className="p-6 border-0 shadow-lg">
                <h3 className="font-semibold text-gray-900 mb-2">
                  How do I contact technical support?
                </h3>
                <p className="text-gray-600">
                  Reach us via WhatsApp, Email (igetalibar@gmail.com), or Phone (18620309005). Business hours: 9 AM to 6 PM.
                </p>
              </Card>

              <Card className="p-6 border-0 shadow-lg">
                <h3 className="font-semibold text-gray-900 mb-2">
                  What if my product doesn't work?
                </h3>
                <p className="text-gray-600">
                  First, verify all connections are correct and try reinserting the pod. If the issue persists, we offer unconditional returns within 7 days.
                </p>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 py-16 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Experience IGET?
          </h2>
          <p className="text-lg text-blue-100 mb-8">
            Choose your favorite flavor and model, and enjoy fast Australian local delivery.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://igetalibar.com" target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto">
                Shop IGET Now
              </Button>
            </a>
            <Link href="/products/all">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto bg-white text-blue-600 hover:bg-gray-100">
                View All Flavors
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Schema.org Product JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "IGET Vapes",
            "description": "IGET vapes with 100+ flavor options and open pod system",
            "brand": {
              "@type": "Brand",
              "name": "IGET"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.7",
              "ratingCount": "10000"
            }
          })
        }}
      />
    </div>
  );
}
